import { GCInstant as platform } from 'src/lib/gcinstant';
import { measureLoadPhase } from 'src/lib/analytics/gameLoadTracking';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import sounds from 'src/lib/sounds';
import { getLaunchScene, isSceneEntered } from 'src/lib/stateUtils';
import { isFirstEntry } from 'src/lib/platformUtils';
import StateObserver, { now } from 'src/StateObserver';
import {
  actionBlock,
  singleAction,
  createLaunchSequence,
} from './LaunchSequence';
import { getPet } from '../pet';
import { getTutorialController } from '../tutorial/TutorialController';
import { shouldRemindNativeAppUpdate } from '../native/native.util';
import { isPlatformNative } from 'src/lib/native/native.util';
import { canClaimResubscriptionReward } from 'src/replicant/chatbot/chatbot.getters';
import { onboardingReturning } from './onboarding.sequence';
import { getApplication, isPlatformGuest } from 'src/lib/utils';
import { captureGenericError } from 'src/lib/sentry';
import statePromise from 'src/lib/statePromise';
import { petIdleSequence } from '../pet/pet.sequence';
import { TOS_VERSION } from 'src/replicant/ruleset/tos';
import { isEditorMode } from '../minigame/match2-timestep/editor/editor.utils';
import { hideLoading, showLoading } from 'src/state/ui';
import {
  puzzleCoinsAutoCompleteTutorials,
  puzzleCoinsFirstEntrySequence,
  puzzleCoinsOpenPetFirstScene,
} from '../puzzlecoins/puzzlecoins.sequence';
import { isLoadingFTUEActive } from 'src/lib/ftue';
import { showUpdateAppReminder } from '../native/native.sequence';
import {
  autoAddFriendFromInvite,
  isEnteringFromInviteV2,
} from '../friends/addFriendsV2.sequence';

/**
 * Launch sequence way simpler than the default one, while puzzleCoins tutorials are not complete
 */
export const puzzleCoinsLaunchSequence = createLaunchSequence([
  /**
   * Start straight into the level editor, if in editor mode.
   */
  async () => {
    // Skip if not in development mode
    if (!isEditorMode()) {
      return false;
    }

    getApplication().screenManager.showScreen('minigame', {
      minigame: 'match2-editor',
    });

    return true;
  },

  /**
   * LINE onboarding — recurring launch prompt (FTUE).
   *
   * Tutorial users run this sequence, so returning-user onboarding is mirrored
   * here. Skip the first entry because its optional profile-consent flow runs at
   * the splash.
   */
  singleAction({
    skip: () =>
      isFirstEntry() || isPlatformGuest(platform) || isPlatformNative(),
    action: async () => {
      await platform.dismissLoadingScreenAsync({
        launchPath: 'puzzle_coins_returning_onboarding',
      });
      await onboardingReturning('launch_sequence');
      return false;
    },
  }),

  /**
   * Resubscribe reward.
   */
  actionBlock({
    skip: ({ user }) =>
      isFirstEntry() || // Never block user to subscribe in their very first entry
      !canClaimResubscriptionReward(user, now()) ||
      !platform.isSubscribedToBot,
    actions: [
      async () => {
        await platform.dismissLoadingScreenAsync({
          launchPath: 'puzzle_coins_resubscribe_reward',
        });
        try {
          await StateObserver.invoke.claimResubscriptionReward();
          await openPopupPromise('popupResubscribeReward', {
            origin: 'launch',
          });
        } catch (e) {
          captureGenericError('claimResubscriptionReward', e);
        }
        return false;
      },
    ],
  }),

  actionBlock({
    enabled: () => shouldRemindNativeAppUpdate(),
    actions: [
      async () => {
        await platform.dismissLoadingScreenAsync({
          launchPath: 'puzzle_coins_update_app_reminder',
        });
        await showUpdateAppReminder('launch');
        return false;
      },
    ],
  }),

  /**
   * Handle Add Friends V2 for new users entering via invite link.
   * Auto-add friend without confirmation popup (new users become friends automatically).
   */
  actionBlock({
    enabled: () => isEnteringFromInviteV2(),
    actions: [
      async () => {
        await autoAddFriendFromInvite();
        return false;
      },
    ],
  }),

  /**
   * Load the initial scene.
   */
  actionBlock({
    // Skip this action if loading FTUE is active
    skip: () => isLoadingFTUEActive(),
    actions: [
      async () => {
        // Auto fix uncompleted tutorials for puzzle coins, if needed.
        puzzleCoinsAutoCompleteTutorials();

        // Open the first pet scene
        await measureLoadPhase('sceneEnter', () =>
          puzzleCoinsOpenPetFirstScene(),
        );

        // Continue with next action.
        return false;
      },
    ],
  }),

  /**
   * Run the puzzleCoins sequence if enabled
   */
  actionBlock({
    actions: [
      async () => {
        await puzzleCoinsFirstEntrySequence();
        return false;
      },
    ],
  }),

  /**
   * Trigger gameStarted tutorial event
   */
  async () => {
    await getTutorialController().handleEvent('gameStarted');
    return false;
  },

  /**
   * Check if ToS was updated, and show popup.
   */
  actionBlock({
    actions: [
      async () => {
        if (await platform.tos.hasAcceptedToS(TOS_VERSION)) return true;

        platform.tos.trackPopupOpenEvent(TOS_VERSION);

        const shownAt = StateObserver.now();

        await openPopupPromise('popupToSUpdated', {});

        platform.tos.acceptToS(TOS_VERSION, {
          requestMs: StateObserver.now() - shownAt,
        });

        return true;
      },
    ],
  }),

  /**
   * Invoke pet idle sequence if we're entering the living room.
   * Should be the last action in the sequence.
   */
  actionBlock({
    enabled: ({ ui }) => ui.transition.nextScene === 'livingRoom',
    actions: [
      async () => {
        await statePromise(() => isSceneEntered('livingRoom'));
        await petIdleSequence();
        return true;
      },
    ],
  }),
]);
